import { createRoot } from 'react-dom/client';
import './index.css';
import { i18nReady } from './i18n/config';
import { createLogger, normalizeUnknownError } from '@/lib/structuredLogger';
import App from './App.tsx';

const logger = createLogger('Main');

function isBenignSupabaseAuthLockError(value: unknown): boolean {
  const normalized = normalizeUnknownError(value);
  const message = normalized.error?.message ?? '';
  const hint = typeof normalized.extraData?.hint === 'string' ? normalized.extraData.hint : '';

  return (
    /lock .*stole it/i.test(message) ||
    /aborterror: lock was stolen by another request/i.test(message) ||
    /request was aborted \(timeout or manual cancellation\)/i.test(hint)
  );
}

// ONE-TIME cache clear to remove old Service Worker caches
// This runs only once per browser to fix old cached API responses
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      // Check if we already cleared caches (one-time operation)
      const cacheCleared = localStorage.getItem('sw_cache_cleared_v3');

      if (!cacheCleared) {
        if (!import.meta.env.PROD) {
          logger.info('Performing one-time cache clear');
        }

        // Unregister ALL existing service workers
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          if (!import.meta.env.PROD) {
            logger.info('Service Worker unregistered');
          }
        }

        // Clear all browser caches (Service Worker caches only, NOT React Query)
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
          if (!import.meta.env.PROD) {
            logger.info('Old Service Worker caches cleared');
          }
        }

        // Mark as completed - won't run again
        localStorage.setItem('sw_cache_cleared_v3', 'true');
        if (!import.meta.env.PROD) {
          logger.info('One-time cache clear completed. Normal caching will work from now on');
        }
      } else {
        if (!import.meta.env.PROD) {
          logger.info('Cache already cleared. App running normally');
        }
      }

      // Note: We're NOT re-registering the service worker to avoid caching issues
      // React Query will handle data caching in memory
    } catch (error) {
      if (!import.meta.env.PROD) {
        logger.info('Cache cleanup failed', { error });
      }
    }
  });
}

// Add error handling for production debugging
window.addEventListener('error', (event) => {
  const normalized = normalizeUnknownError(event.error, event.message || 'Global error');
  const error =
    normalized.error ?? new Error(event.message || 'Global error');

  logger.error('Global error', error, normalized.extraData);
});

window.addEventListener('unhandledrejection', (event) => {
  const normalized = normalizeUnknownError(event.reason, 'Unhandled promise rejection');
  const errorMessage = normalized.error?.message || String(event.reason);

  if (
    errorMessage.includes('MetaMask') ||
    errorMessage.includes('ethereum') ||
    errorMessage.includes('extension')
  ) {
    event.preventDefault(); // Suppress these errors
    return;
  }

  if (isBenignSupabaseAuthLockError(event.reason)) {
    event.preventDefault();
    logger.warn('Suppressed benign Supabase auth lock rejection', normalized.extraData);
    return;
  }

  const error = normalized.error ?? new Error(errorMessage || 'Unhandled promise rejection');
  const extra = normalized.extraData ?? { reason: event.reason };

  logger.error('Unhandled promise rejection', error, extra);
});

// Debug logging for development only
if (!import.meta.env.PROD) {
  logger.info('App starting', {
    nodeEnv: import.meta.env.MODE,
    baseUrl: import.meta.env.BASE_URL,
    timestamp: new Date().toISOString(),
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  logger.error('Root element not found');
  throw new Error('Root element not found');
}

const root = createRoot(rootElement);

async function bootstrapApp() {
  try {
    await i18nReady;
  } catch (error) {
    logger.error('i18n initialization failed', error instanceof Error ? error : undefined, {
      error,
    });
  }

  root.render(<App />);
}

void bootstrapApp();
